import React, { useEffect, useState } from "react";
import { Button, DatePicker, Form, Input, notification } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import B2C2BApi from "../../../../api-services/b2c2b-api.service";
import { Agent } from "../../../../interfaces/agent.interface";
import BudgetRangeField from "../../../../components/shared/BudgetRangeField";
import { BUDGET_MAX, BUDGET_STEP } from "../../../../utils/groundListingParams";
import useBreakpoint from "../../../../utils/useBreakpoint";
import AgentPicker from "./AgentPicker";
import AgentSelect from "./AgentSelect";
import RequiredLabel from "./RequiredLabel";

dayjs.extend(customParseFormat);

const { TextArea } = Input;

export const CUSTOMIZE_BUDGET_MIN = 1000;

export interface TravelDetailsValues {
  travelDate?: string;
  budget: [number, number];
  message: string;
  agentId?: number;
  agentName?: string;
}

interface Props {
  initialDepartureDate?: string;
  submitting: boolean;
  onBack: () => void;
  onSubmit: (values: TravelDetailsValues) => void;
}

export default function TravelDetailsStep({ initialDepartureDate, submitting, onBack, onSubmit }: Props) {
  const [form] = Form.useForm();
  const isMobile = useBreakpoint();
  const [budget, setBudget] = useState<[number, number]>([CUSTOMIZE_BUDGET_MIN, BUDGET_MAX]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  useEffect(() => {
    setAgentsLoading(true);
    B2C2BApi.getEligibleAgents()
      .then((eligibleAgents) => setAgents(eligibleAgents || []))
      .catch(() => notification.error({ message: "An error occured while loading agents." }))
      .finally(() => setAgentsLoading(false));
  }, []);

  const initialDate = initialDepartureDate
    ? dayjs(initialDepartureDate, ["DD/MM/YYYY", "YYYY-MM-DD"], true)
    : undefined;

  const handleFinish = (values: any) => {
    const agent = agents.find((a) => a.id === values.agentId);
    onSubmit({
      travelDate: values.travelDate ? values.travelDate.format("DD/MM/YYYY") : undefined,
      budget,
      message: values.message || "",
      agentId: values.agentId,
      agentName: agent?.name,
    });
  };

  return (
    <div className="customize-tour-form__step-body customize-tour-form__step-body--details">
      <Form
        form={form}
        layout="vertical"
        initialValues={{ travelDate: initialDate?.isValid() ? initialDate : undefined }}
        onFinish={handleFinish}
      >
        <Form.Item
          label={<RequiredLabel>When would you like to travel?</RequiredLabel>}
          name="travelDate"
          required={false}
          rules={[{ required: true, message: "Please choose a date" }]}
        >
          <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" placeholder="Please choose a date (DD/MM/YYYY)" />
        </Form.Item>

        <div className="customize-tour-form__section">
          <h3 className="customize-tour-form__label">
            <RequiredLabel>Your budget per traveler</RequiredLabel>
          </h3>
          <BudgetRangeField
            value={budget}
            onChange={setBudget}
            min={CUSTOMIZE_BUDGET_MIN}
            max={BUDGET_MAX}
            step={BUDGET_STEP}
            rangeClassName="customize-tour-form__budget-range"
            fieldClassName="customize-tour-form__budget-field"
          />
        </div>

        <Form.Item label="Anything we should know?" name="message" className="customize-tour-form__message-input">
          <TextArea rows={4} placeholder="Preferred hotels, accessibility needs, dietary requirements, celebrations, or must-see places." />
        </Form.Item>

        <Form.Item
          label={<RequiredLabel>Any preferred travel agent?</RequiredLabel>}
          name="agentId"
          required={false}
          rules={[{ required: true, message: "Please select a preferred travel agent" }]}
        >
          {isMobile ? (
            <AgentPicker agents={agents} loading={agentsLoading} />
          ) : (
            <AgentSelect agents={agents} loading={agentsLoading} />
          )}
        </Form.Item>

        <div className="customize-tour-form__actions">
          <Button className="customize-tour-form__btn-back" onClick={onBack}>
            Back
          </Button>
          <Button type="primary" htmlType="submit" loading={submitting} className="customize-tour-form__btn-continue">
            Send My Trip Request
          </Button>
        </div>
      </Form>
    </div>
  );
}
